import { Holding, normalizeWatchlistSymbol, Watchlist, WatchlistSymbol } from '@benzinga/watchlist-manager';

export const isCreateWatchlistAction = (action: string | null): boolean =>
  action === 'create-watchlist' || action === 'new_watchlist';

export const parseTickerInput = (value: string): string[] =>
  Array.from(
    new Set(
      value
        .split(/[\s,;]+/)
        .map(ticker => ticker.trim().toUpperCase())
        .filter(Boolean),
    ),
  );

export const removeExistingTickers = (tickers: string[], symbols: WatchlistSymbol[]): string[] => {
  const existingTickers = new Set(symbols.map(symbol => normalizeWatchlistSymbol(symbol.symbol)));
  return tickers.filter(ticker => !existingTickers.has(normalizeWatchlistSymbol(ticker)));
};

export const symbolHasHolding = (symbol: Pick<WatchlistSymbol, 'holding'>): boolean => {
  if (!symbol.holding) return false;
  const holdings = Array.isArray(symbol.holding) ? symbol.holding : [symbol.holding];
  return holdings.some(holding => holding.quantity > 0);
};

export const getSummaryWatchlist = (overviewWatchlist: Watchlist | null, watchlists: Watchlist[]): Watchlist | null =>
  overviewWatchlist ?? watchlists[0] ?? null;

export type AggregatedHolding = {
  costBasis: number;
  fallbackPrice: number;
  quantity: number;
  symbol: string;
};

export type PricedHolding = AggregatedHolding & {
  companyName?: string;
  marketValue: number;
  price: number;
};

export type PortfolioHoldingValues = {
  costBasis: number;
  pricedCount: number;
  symbolValues: Map<string, number>;
  totalValue: number;
  unpricedCount: number;
};

export const aggregateHoldingsBySymbol = (watchlists: Pick<Watchlist, 'symbols'>[]): AggregatedHolding[] => {
  const holdingsBySymbol = new Map<string, AggregatedHolding>();

  watchlists.forEach(watchlist => {
    watchlist.symbols.forEach(symbol => {
      const holdings = Array.isArray(symbol.holding) ? symbol.holding : symbol.holding ? [symbol.holding] : [];
      holdings.forEach(holding => {
        if (holding.quantity <= 0) return;
        const current = holdingsBySymbol.get(symbol.symbol) ?? {
          costBasis: 0,
          fallbackPrice: 0,
          quantity: 0,
          symbol: symbol.symbol,
        };
        const nextQuantity = current.quantity + holding.quantity;
        const fallbackContribution =
          holding.price > 0 ? holding.price : holding.quantity > 0 ? holding.costBasis / holding.quantity : 0;
        if (fallbackContribution > 0 && nextQuantity > 0) {
          current.fallbackPrice =
            (current.fallbackPrice * current.quantity + fallbackContribution * holding.quantity) / nextQuantity;
        }
        current.costBasis += holding.costBasis;
        current.quantity = nextQuantity;
        holdingsBySymbol.set(symbol.symbol, current);
      });
    });
  });

  return Array.from(holdingsBySymbol.values());
};

export const priceHoldings = (
  holdings: AggregatedHolding[],
  pricesBySymbol: Record<string, number>,
  companyNamesBySymbol: Record<string, string> = {},
): PricedHolding[] =>
  holdings
    .map(holding => {
      const quotePrice =
        pricesBySymbol[holding.symbol] ?? pricesBySymbol[normalizeWatchlistSymbol(holding.symbol)] ?? 0;
      const price = quotePrice > 0 ? quotePrice : holding.fallbackPrice > 0 ? holding.fallbackPrice : 0;
      const companyName =
        companyNamesBySymbol[holding.symbol] ?? companyNamesBySymbol[normalizeWatchlistSymbol(holding.symbol)];
      return {
        ...holding,
        companyName,
        marketValue: price > 0 ? holding.quantity * price : 0,
        price,
      };
    })
    .sort((firstHolding, secondHolding) => secondHolding.marketValue - firstHolding.marketValue);

export const sumPricedHoldingValues = (holdings: PricedHolding[]): PortfolioHoldingValues => {
  let costBasis = 0;
  let totalValue = 0;
  let pricedCount = 0;
  let unpricedCount = 0;
  const symbolValues = new Map<string, number>();

  holdings.forEach(holding => {
    if (holding.price <= 0 || holding.marketValue <= 0) {
      unpricedCount += 1;
      return;
    }
    pricedCount += 1;
    costBasis += holding.costBasis;
    totalValue += holding.marketValue;
    symbolValues.set(holding.symbol, holding.marketValue);
  });

  return { costBasis, pricedCount, symbolValues, totalValue, unpricedCount };
};

export const getHoldingQuantity = (holdings: Holding | Holding[] | null | undefined): number =>
  Array.isArray(holdings)
    ? holdings.reduce((total, holding) => total + (holding.quantity ?? 0), 0)
    : (holdings?.quantity ?? 0);

const hasPositiveHoldingQuantity = (quantity: number): boolean => quantity > 0;

export const hasPositiveHolding = (holding: Holding | Holding[] | null | undefined): boolean =>
  hasPositiveHoldingQuantity(getHoldingQuantity(holding));

export const hasLinkedHoldings = (watchlists: Pick<Watchlist, 'symbols'>[]): boolean =>
  watchlists.some(watchlist => watchlist.symbols.some(symbol => hasPositiveHolding(symbol.holding)));

export type PortfolioHeaderCounts = {
  heldSymbolCount: number;
  portfolioCount: number;
};

export const portfolioHeaderCounts = (watchlists: Pick<Watchlist, 'symbols'>[]): PortfolioHeaderCounts => {
  const heldSymbols = new Set<string>();

  watchlists.forEach(watchlist => {
    watchlist.symbols.forEach(symbol => {
      if (hasPositiveHolding(symbol.holding)) heldSymbols.add(symbol.symbol);
    });
  });

  return {
    heldSymbolCount: heldSymbols.size,
    portfolioCount: watchlists.filter(watchlist => hasLinkedHoldings([watchlist])).length,
  };
};

export const getHistoryChartWeight = (holdings: Holding | Holding[] | null | undefined): number =>
  getHoldingQuantity(holdings) || 1;

export const getHoldingCostBasis = (holdings: Holding | Holding[] | null | undefined): number =>
  Array.isArray(holdings)
    ? holdings.reduce((total, holding) => total + (holding.costBasis ?? 0), 0)
    : (holdings?.costBasis ?? 0);

export const averageChangePercent = (changes: number[]): number | null => {
  const values = changes.filter(value => Number.isFinite(value));
  if (values.length === 0) return null;
  return values.reduce((total, value) => total + value, 0) / values.length;
};

export type DailyChangePosition = {
  changePercent: number;
  marketValue: number;
  symbol?: string;
};

export type DailyChangeWeighting = 'portfolio' | 'watchlist';

export type DailyChangeLeader = {
  changePercent: number;
  symbol: string;
};

export type DailyChangeResult = {
  downCount: number;
  leader: DailyChangeLeader | null;
  percent: number;
  upCount: number;
  weighting: DailyChangeWeighting;
};

export type DailyChangeQuoteInput = {
  changePercent?: number | null;
  costBasis?: number;
  lastTradePrice?: number | null;
  previousClosePrice?: number | null;
  quantity: number;
  symbol?: string;
};

export const buildDailyChangePosition = (input: DailyChangeQuoteInput): DailyChangePosition | null => {
  const hasHolding = hasPositiveHoldingQuantity(input.quantity);
  const changePercent =
    typeof input.changePercent === 'number' && Number.isFinite(input.changePercent) ? input.changePercent : null;

  if (!hasHolding && changePercent === null) return null;

  const previousClose =
    typeof input.previousClosePrice === 'number' && input.previousClosePrice > 0
      ? input.previousClosePrice
      : null;
  const lastTrade =
    typeof input.lastTradePrice === 'number' && input.lastTradePrice > 0 ? input.lastTradePrice : null;
  const priceForWeight = previousClose ?? lastTrade;
  const costBasis = input.costBasis ?? 0;

  let marketValue = 0;
  if (hasHolding) {
    if (priceForWeight != null) {
      marketValue = input.quantity * priceForWeight;
    } else if (costBasis > 0) {
      marketValue = costBasis;
    }
  }

  return {
    changePercent: changePercent ?? 0,
    marketValue,
    ...(input.symbol ? { symbol: input.symbol } : {}),
  };
};

const countSignedMoves = (source: DailyChangePosition[]): Pick<DailyChangeResult, 'downCount' | 'upCount'> => ({
  downCount: source.reduce((total, position) => total + (position.changePercent < 0 ? 1 : 0), 0),
  upCount: source.reduce((total, position) => total + (position.changePercent > 0 ? 1 : 0), 0),
});

const pickDailyChangeLeader = (
  source: DailyChangePosition[],
  scoreFor: (position: DailyChangePosition) => number,
): DailyChangeLeader | null => {
  let leader: DailyChangeLeader | null = null;
  let bestScore = 0;

  source.forEach(position => {
    if (!position.symbol) return;
    const score = Math.abs(scoreFor(position));
    if (score <= bestScore) return;
    bestScore = score;
    leader = { changePercent: position.changePercent, symbol: position.symbol };
  });

  return leader;
};

export const resolveDailyChangePercent = (positions: DailyChangePosition[]): DailyChangeResult | null => {
  const withChange = positions.filter(position => Number.isFinite(position.changePercent));
  if (withChange.length === 0) return null;

  const heldPositions = withChange.filter(position => position.marketValue > 0);
  const totalMarketValue = heldPositions.reduce((total, position) => total + position.marketValue, 0);

  if (totalMarketValue > 0) {
    const weightedSum = heldPositions.reduce(
      (total, position) => total + position.changePercent * position.marketValue,
      0,
    );
    const breadth = countSignedMoves(heldPositions);
    return {
      ...breadth,
      leader: pickDailyChangeLeader(
        heldPositions,
        position => (position.changePercent * position.marketValue) / totalMarketValue,
      ),
      percent: weightedSum / totalMarketValue,
      weighting: 'portfolio',
    };
  }

  const equalWeight = averageChangePercent(withChange.map(position => position.changePercent));
  if (equalWeight === null) return null;
  const breadth = countSignedMoves(withChange);
  return {
    ...breadth,
    leader: pickDailyChangeLeader(withChange, position => position.changePercent),
    percent: equalWeight,
    weighting: 'watchlist',
  };
};

export type SectorAllocation = {
  color: string;
  label: string;
  value: number;
};

const ALLOCATION_COLORS = ['#3b6fd4', '#2f9f78', '#d4a017', '#7c6bc4', '#7a8699'];

export const buildWeightedAllocations = (valueByLabel: Map<string, number>): SectorAllocation[] => {
  const sorted = Array.from(valueByLabel.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((first, second) => second.value - first.value);

  if (sorted.length === 0) return [];

  const rows = sorted.slice(0, 4).map(allocation => ({ ...allocation }));
  const overflowValue = sorted.slice(4).reduce((total, allocation) => total + allocation.value, 0);

  if (overflowValue > 0) {
    const otherRow = rows.find(allocation => allocation.label === 'Other');
    if (otherRow) {
      otherRow.value += overflowValue;
    } else {
      rows.push({ label: 'Other', value: overflowValue });
    }
  }

  return rows
    .sort((first, second) => second.value - first.value)
    .map((allocation, index) => ({
      ...allocation,
      color: ALLOCATION_COLORS[index] ?? ALLOCATION_COLORS[ALLOCATION_COLORS.length - 1],
    }));
};

export const buildSectorAllocations = (
  symbolValues: Map<string, number>,
  sectorBySymbol: Record<string, string>,
): SectorAllocation[] => {
  const sectorValues = new Map<string, number>();

  symbolValues.forEach((value, symbol) => {
    if (value <= 0) return;
    const sector = sectorBySymbol[symbol.toUpperCase()] ?? 'Other';
    sectorValues.set(sector, (sectorValues.get(sector) ?? 0) + value);
  });

  return buildWeightedAllocations(sectorValues);
};

const MIN_CLASSIFIED_ALLOCATION_SHARE = 0.5;

export const buildPortfolioAllocations = (
  symbolValues: Map<string, number>,
  sectorBySymbol: Record<string, string>,
): SectorAllocation[] => {
  let classifiedValue = 0;
  let totalValue = 0;

  symbolValues.forEach((value, symbol) => {
    if (value <= 0) return;
    totalValue += value;
    const sector = sectorBySymbol[symbol.toUpperCase()];
    if (sector && sector !== 'Other') classifiedValue += value;
  });

  if (totalValue <= 0) return [];

  if (classifiedValue / totalValue < MIN_CLASSIFIED_ALLOCATION_SHARE) {
    return buildWeightedAllocations(symbolValues);
  }

  return buildSectorAllocations(symbolValues, sectorBySymbol);
};

export const shortenSectorName = (sector: string): string => {
  const normalized = sector.trim().toLowerCase();
  if (normalized.includes('technolog')) return 'Technology';
  if (normalized.includes('communication') || normalized.includes('telecom')) return 'Comm';
  if (normalized.includes('retail')) return 'Retail';
  if (normalized.includes('consumer')) return 'Consumer';
  if (normalized.includes('finance') || normalized.includes('financial') || normalized.includes('insuranc')) {
    return 'Financials';
  }
  if (normalized.includes('health')) return 'Healthcare';
  if (normalized.includes('industri') || normalized.includes('manufactur')) return 'Industrials';
  if (normalized.includes('transport')) return 'Transport';
  if (normalized.includes('energy')) return 'Energy';
  if (normalized.includes('utilit')) return 'Utilities';
  if (normalized.includes('real estate') || normalized.includes('reit')) return 'Real estate';
  if (normalized.includes('material') || normalized.includes('mining')) return 'Materials';
  if (normalized.includes('agricultur') || normalized.includes('forest')) return 'Agriculture';
  if (normalized.includes('service')) return 'Services';
  if (!sector || normalized === 'unknown' || normalized === 'other') return 'Other';
  return sector.length > 18 ? `${sector.slice(0, 16)}…` : sector;
};

export const allocationLabelFromQuote = (quote: {
  sector?: string | null;
  type?: string | null;
}): string => {
  if (quote.sector) return shortenSectorName(quote.sector);
  const normalizedType = quote.type?.trim().toUpperCase();
  if (normalizedType === 'ETF') return 'ETFs';
  if (normalizedType === 'MUTUAL FUND' || normalizedType === 'FUND') return 'Funds';
  if (normalizedType === 'MONEY MARKET') return 'Cash';
  return 'Other';
};

export const buildSectorBySymbol = (
  details: Array<{
    sector?: { sector_name?: string } | null;
    symbol?: string | null;
    tickers?: string[] | null;
  }>,
): Record<string, string> => {
  const sectorBySymbol: Record<string, string> = {};

  details.forEach(detail => {
    const sector = shortenSectorName(detail.sector?.sector_name ?? 'Other');
    const symbols = [...(detail.symbol ? [detail.symbol] : []), ...(detail.tickers ?? [])];

    symbols.forEach(symbol => {
      const normalized = symbol.trim().toUpperCase();
      if (!normalized) return;
      sectorBySymbol[normalized] = sector;
    });
  });

  return sectorBySymbol;
};
